import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { components } from "@dolfin/api-public";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import {
  AddressFields,
  addressToRequest,
  addressToValue,
  emptyAddress,
  type AddressValue,
} from "@/components/AddressFields";
import {
  BankDetailsFields,
  bankDetailsToRequest,
  bankDetailsToValue,
  emptyBankDetails,
  type BankDetailsValue,
} from "@/components/BankDetailsFields";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";

type SupplierResponse = components["schemas"]["SupplierResponse"];

export interface EditSupplierDialogProps {
  supplier: SupplierResponse | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated: () => void;
}

export function EditSupplierDialog({
  supplier,
  open,
  onOpenChange,
  onUpdated,
}: EditSupplierDialogProps) {
  const api = useDolfinClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [taxNumber, setTaxNumber] = useState("");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [address, setAddress] = useState<AddressValue>(emptyAddress);
  const [bankDetails, setBankDetails] = useState<BankDetailsValue>(emptyBankDetails);
  const [paymentTermsDefault, setPaymentTermsDefault] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function populateFromSupplier() {
    setName(supplier?.name ?? "");
    setEmail(supplier?.email ?? "");
    setPhone(supplier?.phone ?? "");
    setWebsite(supplier?.website ?? "");
    setTaxNumber(supplier?.taxNumber ?? "");
    setRegistrationNumber(supplier?.registrationNumber ?? "");
    setAddress(addressToValue(supplier?.address));
    setBankDetails(bankDetailsToValue(supplier?.bankDetails));
    setPaymentTermsDefault(supplier?.paymentTermsDefault ?? "");
    setError(null);
  }

  useEffect(() => {
    if (supplier) populateFromSupplier();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier]);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      populateFromSupplier();
      setSubmitting(false);
    }
    onOpenChange(nextOpen);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!supplier) return;
    setError(null);

    if (!name.trim()) {
      setError("Name is required.");
      return;
    }

    setSubmitting(true);
    const { error: apiError } = await api.PATCH("/v1/suppliers/{id}", {
      params: { path: { id: supplier.id! } },
      body: {
        name: name.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        website: website.trim() || null,
        taxNumber: taxNumber.trim() || null,
        registrationNumber: registrationNumber.trim() || null,
        address: addressToRequest(address),
        bankDetails: bankDetailsToRequest(bankDetails),
        paymentTermsDefault: paymentTermsDefault.trim() || null,
      },
    });

    if (apiError) {
      const message = apiErrorMessage(apiError, "Failed to update supplier.");
      setError(message);
      toast.error(message);
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    handleOpenChange(false);
    onUpdated();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Supplier</DialogTitle>
            <DialogDescription>Update supplier details.</DialogDescription>
          </DialogHeader>

          <div className="mt-4 max-h-[65vh] space-y-4 overflow-y-auto pr-1">
            <div className="space-y-2">
              <Label htmlFor="edit-supplier-name">Name</Label>
              <Input
                id="edit-supplier-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-supplier-email">Email</Label>
                <Input
                  id="edit-supplier-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-supplier-phone">Phone</Label>
                <Input
                  id="edit-supplier-phone"
                  placeholder="e.g. +44 20 1234 5678"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-supplier-website">Website</Label>
                <Input
                  id="edit-supplier-website"
                  placeholder="e.g. acme.com"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-supplier-payment-terms">Payment Terms</Label>
                <Input
                  id="edit-supplier-payment-terms"
                  placeholder="e.g. Net 30"
                  value={paymentTermsDefault}
                  onChange={(e) => setPaymentTermsDefault(e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-supplier-tax">Tax Number</Label>
                <Input
                  id="edit-supplier-tax"
                  placeholder="e.g. GB123456789"
                  value={taxNumber}
                  onChange={(e) => setTaxNumber(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-supplier-registration">Registration Number</Label>
                <Input
                  id="edit-supplier-registration"
                  placeholder="e.g. 01234567"
                  value={registrationNumber}
                  onChange={(e) => setRegistrationNumber(e.target.value)}
                />
              </div>
            </div>

            <AddressFields value={address} onChange={setAddress} />

            <BankDetailsFields value={bankDetails} onChange={setBankDetails} />

            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>

          <DialogFooter className="mt-6">
            <Button type="submit" disabled={submitting}>
              {submitting && <Spinner className="size-4" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
